import { task } from 'mobx-task'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { DocumentType } from '@/features/documents/DocumentType'
import {
  type InventoryStore,
  type UpdateInventoryItemParams,
} from '@/features/inventory/InventoryStore'
import { InventoryItemDialogViewModelBase } from '@/features/inventory/admin/inventory-item-dialog/InventoryItemDialogViewModelBase'
import { type RefreshInventoryItems } from '@/features/inventory/admin/InventorySearchViewModel'

/**
 * Edits a service item.
 */
export class EditInventoryItemDialogViewModel extends InventoryItemDialogViewModelBase {
  constructor(
    private readonly _inventoryStore: InventoryStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _refreshItems: RefreshInventoryItems,
    private readonly _getSelectedWorkspace: () => Workspace | undefined,
    getDocumentTypes: () => DocumentType[],
  ) {
    super(getDocumentTypes)
  }

  /**
   * Updates the inventory item
   */
  submit = task.resolved(async () => {
    if (!this.item || !this.validate()) {
      return
    }

    const updatedItem: UpdateInventoryItemParams = {
      name: this.name,
      description: this.description,
      courtesyAmount: this.courtesyQuantity,
      pricing: this.pricingVm.toValues(),
      supportingItems: this.supportingItems,
      proVisibility: this.hideFromPro ? 'Hidden' : 'Visible',
      typeSpecification: this.getInventoryItemTypeSpecification(),
    }

    await this._inventoryStore
      .updateInventoryItem(this.item.id, updatedItem)
      .catch(this._createAlertErrorHandler('Editing the item failed'))

    this._newAlert()
      .withState('success')
      .saying('Success', 'Item edited.')
      .autoDismiss()
      .show()

    await this._refreshItems(this._getSelectedWorkspace() ?? Workspace.none)

    this.modal.close()
  })
}
